import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { User, ChevronDown, ChevronUp, ArrowLeftRight, CircleX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'

import { ChannelMember as ChannelMemberType, ChannelType, getChannelId } from '../../../../shared-src/channel'
import { getUserDisplayNameWithDisambiguation } from '../../../../shared-src/user'
import { getColorSet } from '@/theme/color'
import { useChatStore } from '@/state/chat-store'
import { useUserStore } from '@/state/user-store'
import { getFeathersClient } from './feathers-client'

interface ChannelMemberProps {
	member: ChannelMemberType
	//	All members in the same channel context — used to disambiguate display
	//	names if two members share one. Should include the target member; the
	//	collision check skips self by userId.
	allMembers: ChannelMemberType[]
}

export function ChannelMember({ member, allMembers }: ChannelMemberProps) {
	const [isExpanded, setIsExpanded] = useState(false)
	const [isInitiatingDm, setIsInitiatingDm] = useState(false)
	const [isVoting, setIsVoting] = useState(false)
	const [voteDialogOpen, setVoteDialogOpen] = useState(false)
	const colorSet = getColorSet({ senderColor: member.color })
	const isLimitedMember = member.membershipStatus === 'limited'
	const activeChannel = useChatStore.getState().activeChannel
	const isMyMembershipLimited = activeChannel?.myMembershipStatus === 'limited'

	//	hide "Start DM" when a DM channel already exists with this member
	//	(any status: pending, accepted, or rejected — backend rejects re-initiation in all cases)
	//
	//	`some()` over an unloaded list answers `false` -- "no DM exists" -- which is the SAME lie the
	//	sidebar tells, but with a worse outcome: the button is offered for a member the user already
	//	DMs, `initiate-dm` is refused by the backend, and the catch below tells them to try again,
	//	which cannot ever work (ha-437). So the answer is only trustworthy once the list is loaded,
	//	and until then the button is suppressed rather than shown on a guess.
	const myUserId = useUserStore(state => state.user?.userId)
	const channelsStatus = useChatStore(state => state.channelsStatus)
	const hasExistingDm = useChatStore(state => {
		if (!myUserId) return false
		const dmChannelId = getChannelId({ userId1: myUserId, userId2: member.userId })
		return state.channels.some(c => c.id === dmChannelId)
	})
	const canStartDm = channelsStatus === 'loaded' && !hasExistingDm

	const toggleExpanded = () => {
		setIsExpanded(!isExpanded)
	}

	const handleStartDm = async () => {
		setIsInitiatingDm(true)
		try {
			const feathersClient = getFeathersClient()
			const channelService = feathersClient.service('channels')

			const activeChannel = useChatStore.getState().activeChannel
			if (!activeChannel) {
				throw new Error('No active channel')
			}

			const channelType = ChannelType.fromChannelId(activeChannel.id)
			if (channelType !== ChannelType.hangaroundGroup) {
				throw new Error('Can only initiate DMs from group channels')
			}

			const groupId = activeChannel.id.slice(3)

			await channelService.create({
				action: 'initiate-dm',
				targetUserId: member.userId,
				groupId: groupId
			})

			setIsExpanded(false)
		} catch (error) {
			console.error('Failed to initiate DM:', error)
			toast.error('Failed to start DM. Please try again.')
		} finally {
			setIsInitiatingDm(false)
		}
	}

	const handleVoteToRemove = async () => {
		setIsVoting(true)
		try {
			const feathersClient = getFeathersClient()
			const channelService = feathersClient.service('channels')

			const activeChannel = useChatStore.getState().activeChannel
			if (!activeChannel) {
				throw new Error('No active channel')
			}

			const channelType = ChannelType.fromChannelId(activeChannel.id)
			if (channelType !== ChannelType.hangaroundGroup) {
				throw new Error('Can only vote to remove members from group channels')
			}

			await channelService.patch(activeChannel.id, {
				action: 'vote-to-remove',
				targetUserId: member.userId
			})
		} catch (error) {
			console.error('Failed to vote to remove member:', error)
		} finally {
			setIsVoting(false)
			setVoteDialogOpen(false)
			setIsExpanded(false)
		}
	}

	return (
		<div 
			className={`transition-all duration-200 
				${isExpanded ? 'shadow-messageInput' : ''}
			`}
			style={{ 
				backgroundColor: isExpanded ? colorSet.background : 'transparent',
				color: colorSet.dark 
			}}
		>
			<div 
				className="flex items-center justify-between py-2 mt-1 mx-2 select-none cursor-pointer"
				onClick={toggleExpanded}
			>
				<div className={`flex items-center gap-2
					${isLimitedMember ? 'opacity-50' : ''}
				`}>
					<User className="h-4 w-4" />
					<p>{getUserDisplayNameWithDisambiguation({ target: member, allUsers: allMembers })}</p>
				</div>
				{isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
			</div>
			
			{isExpanded && (
				<div className="flex flex-col gap-2 mt-1 mb-4 mx-2">
					<p className="text-sm">User Id: {member.userId}</p>
					{!isLimitedMember && canStartDm && (
						<Button
							className="w-full py-1 text-sm rounded border hover:opacity-80 shadow-md"
							style={{
								borderColor: colorSet.border,
								backgroundColor: 'white',
								color: colorSet.dark
							}}
							onClick={handleStartDm}
							disabled={isMyMembershipLimited || isInitiatingDm}
						>
							<ArrowLeftRight className="h-4 w-4 mr-2" />
							{isInitiatingDm ? 'Starting DM...' : 'Start DM'}
						</Button>
					)}
					<ConfirmationDialog
						open={voteDialogOpen}
						onOpenChange={setVoteDialogOpen}
						title="Vote to Remove Member"
						description={`Are you sure you want to vote to remove ${member.name ?? member.userId} from this group?`}
						confirmText={isVoting ? 'Voting...' : 'Vote to Remove'}
						onConfirm={handleVoteToRemove}
						isLoading={isVoting}
						trigger={
							<Button
								className="w-full py-1 text-sm rounded border hover:opacity-80 shadow-md"
								style={{
									borderColor: colorSet.border,
									backgroundColor: 'white',
									color: colorSet.dark
								}}
								disabled={isMyMembershipLimited || isVoting}
							>
								<CircleX className="h-4 w-4 mr-2" />
								Vote to Remove
							</Button>
						}
					/>
				</div>
			)}
		</div>
	)
}